// eslint-disable-next-line import-x/named -- HttpStatusCode is a valid axios export; false positive from .d.cts resolution
import { AxiosError, HttpStatusCode } from 'axios';
import { OptionsObject, useSnackbar as useNotistackSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

type Method = 'get' | 'options' | 'post' | 'put' | 'patch' | 'delete';
interface ErrorMessage {
	httpStatusCode: number | 'all';
	message: string;
	method?: Method;
}

const useSnackbar = (_overrides?: ErrorMessage[]) => {
	const { t } = useTranslation();
	const snackbar = useNotistackSnackbar();

	const messages: ErrorMessage[] = [
		{
			httpStatusCode: HttpStatusCode.Unauthorized,
			message: t('unauthorized', { ns: 'status' }),
		},
		{
			httpStatusCode: HttpStatusCode.Forbidden,
			message: t('forbidden', { ns: 'status' }),
		},
		{
			httpStatusCode: HttpStatusCode.NotFound,
			message: t('notFound', { ns: 'status' }),
		},
		{
			httpStatusCode: HttpStatusCode.Conflict,
			message: t('thisNameAlreadyExists', { ns: 'errors' }),
			method: 'post',
		},
		{
			// TODO: need to makes this more generic than just access rules
			httpStatusCode: HttpStatusCode.Conflict,
			message: t('deletionPartOfAccessRuleErrorMessage', { ns: 'errors' }),
			method: 'delete',
		},
		{
			httpStatusCode: HttpStatusCode.InternalServerError,
			message: t('somethingWentWrong', { ns: 'errors' }),
		},
		{
			httpStatusCode: HttpStatusCode.BadGateway,
			message: t('somethingWentWrong', { ns: 'errors' }),
		},
	];

	const enqueueSuccessSnackbar = (message: string, options?: OptionsObject<'success'>) => {
		snackbar.enqueueSnackbar(message, { ...options, variant: 'success' });
	};

	const enqueueErrorSnackbar = (message: string, options?: OptionsObject<'error'>) => {
		snackbar.enqueueSnackbar(message, { ...options, variant: 'error' });
	};

	/**
	 *
	 * @param error
	 * @param message Override the default message
	 */
	const enqueueAxiosErrorSnackbar = (
		error: AxiosError,
		message?: string,
		options?: OptionsObject<'error'>
	) => {
		console.error(error);

		if (message) {
			snackbar.enqueueSnackbar(message, {
				variant: 'error',
			});
			return;
		}

		const wasRetried = Boolean((error.config as unknown as { _retry?: boolean })?._retry);
		const isAuthStatus =
			error?.status === HttpStatusCode.Unauthorized || error?.status === HttpStatusCode.Forbidden;
		if (wasRetried && isAuthStatus) {
			return;
		}

		const method = error.response?.config.method;
		const errorMessage: ErrorMessage | undefined = messages.find(
			(el) => el.httpStatusCode === error?.status && (el.method == null || el.method === method)
		);
		snackbar.enqueueSnackbar(errorMessage?.message ?? t('somethingWentWrong', { ns: 'errors' }), {
			...options,
			variant: 'error',
		});
	};

	return {
		...snackbar,
		enqueueErrorSnackbar: enqueueErrorSnackbar,
		enqueueSuccessSnackbar: enqueueSuccessSnackbar,
		enqueueAxiosErrorSnackbar: enqueueAxiosErrorSnackbar,
	};
};

export default useSnackbar;
